import { UserContext } from "@context/User/UserContext";
import { getLngCode } from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  IconButton,
  LinearProgress,
  Modal,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useChatSummary } from "@services/chatBackend";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ModalSummaryProps {
  sessionId: string | null;
  open: boolean;
  onClose: () => void;
}

export const ModalSummary = ({
  sessionId,
  open,
  onClose
}: ModalSummaryProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { currentUser } = useContext(UserContext);

  const [summary, setSummary] = useState("");

  const language = getLngCode(currentUser.language);

  const { data, isFetching, isError } = useChatSummary(
    sessionId || "",
    language,
    open
  );

  useEffect(() => {
    if (!isFetching && !isError && data) {
      setSummary(data.outputs[0].outputs[0].outputs.text.message);
    }
  }, [data, isFetching, isError]);

  const handleCopy = () => {
    if (summary) {
      void navigator.clipboard.writeText(summary);
    }
  };

  const handleClose = () => {
    setSummary("");
    onClose();
  };

  if (!sessionId) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          p: 3,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Typography variant="h6">
            {t("chatbot.summary.steps.summary_download")}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {isFetching && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress
              color="primary"
              sx={{ mb: 1, backgroundColor: theme.palette.grey[200] }}
            />
            <Typography variant="body2" color="text.secondary">
              {t("chatbot.summary.steps.processing_request")}
            </Typography>
          </Box>
        )}

        <TextField
          label={t("chatbot.summary.summary_content")}
          multiline
          minRows={6}
          maxRows={12}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Tooltip title={t("chatbot.common.copy_clipboard")}>
            <IconButton
              onClick={handleCopy}
              disabled={!summary}
              color="primary"
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Modal>
  );
};

